import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import axios from "axios";
import Slider from "react-slick";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import PropTypes from "prop-types";
import { renderMedia, isVideoFile, isYouTubeUrl } from "../utils/mediaUtils";
import ImageModal from "../components/ImageModal";

function NewsNextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: "block", marginRight: "-100px" }}
      onClick={onClick}
    />
  );
}

NewsNextArrow.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

function NewsPrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: "block", marginLeft: "-100px" }}
      onClick={onClick}
    />
  );
}

NewsPrevArrow.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

function Page3() {
  // 切換到 /page3 或 id 變更時，強制滾動至頂部
  const locationForScroll = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [locationForScroll.pathname, locationForScroll.search]);

  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [articleContent, setArticleContent] = useState({});
  const [sliderContent, setSliderContent] = useState([]);
  const videoRef = useRef(null);
  const triggerRef = useRef(null);
  const [playedOnce, setPlayedOnce] = useState(false);
  const mainSliderContainerRef = useRef(null);
  let location = useLocation();

  const handleImageClick = (imageSrc, imageAlt, imageIndex = 0) => {
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setModalImageIndex(imageIndex);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    // 關閉時同步外部輪播位置
    const targetIndex = modalImageIndex;
    if (typeof targetIndex === 'number' && !Number.isNaN(targetIndex)) {
      if (nav1 && typeof nav1.slickGoTo === 'function') nav1.slickGoTo(targetIndex);
      if (nav2 && typeof nav2.slickGoTo === 'function') nav2.slickGoTo(targetIndex);
      setCurrentSlideIndex(targetIndex);
    }
    setIsImageModalOpen(false);
  };

  const handlePreviousImage = () => {
    if (!sliderContent || sliderContent.length === 0) return;
    const total = sliderContent.length;
    const newIndex = (modalImageIndex - 1 + total) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(sliderContent[newIndex].image);
    setModalImageAlt(sliderContent[newIndex].title || "");
  };

  const handleNextImage = () => {
    if (!sliderContent || sliderContent.length === 0) return;
    const total = sliderContent.length;
    const newIndex = (modalImageIndex + 1) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(sliderContent[newIndex].image);
    setModalImageAlt(sliderContent[newIndex].title || "");
  };

  useEffect(() => {
    let searchParams = new URLSearchParams(location.search);
    let id = searchParams.get("id");

    axios
      .post("/api/website/getPage", { id: id * 1 })
      .then((response) => {
        const pageData = response.data.data.result;
        setArticleContent(pageData);
        const sliderData = response.data.data.slider;
        setSliderContent(sliderData);
      })
      .catch((error) => {
        console.error("Error fetching article content:", error);
      });

    const currentTrigger = triggerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && videoRef.current && !playedOnce) {
          videoRef.current.play().catch(error => {
            console.log("影片自動播放失敗:", error);
          });
          setPlayedOnce(true);
        }
      },
      {
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [playedOnce, location.search]);

  // 主輪播切換時，暫停主視區域內所有影片
  useEffect(() => {
    if (!mainSliderContainerRef.current) return;
    const container = mainSliderContainerRef.current;
    const videos = container.querySelectorAll('video');
    videos.forEach((vid) => {
      if (!vid.paused) {
        if (typeof vid.pause === 'function') {
          vid.pause();
        }
      }
    });
    // 暫停所有 YouTube（若有）
    const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
    iframes.forEach((ifr) => {
      try {
        ifr.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
      } catch (e) { void e; }
    });
    // 播放目前 slick-current 的 YouTube 影片（若有）
    const currentIframe = container.querySelector('.slick-current iframe[src*="youtube.com/embed"]');
    if (currentIframe) {
      try {
        currentIframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "mute", args: [] }), "*");
        currentIframe.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
      } catch (e) { void e; }
    }
  }, [currentSlideIndex]);

  // 監聽 YouTube 播放狀態，結束後自動切到下一張
  useEffect(() => {
    const onMessage = (event) => {
      let data = null;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch { data = null; }
      if (!data || data.event !== "onStateChange" || data.info !== 0) return; // 0: ended
      const container = mainSliderContainerRef.current;
      if (!container) return;
      const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
      let isFromHere = false;
      iframes.forEach((ifr) => {
        if (isFromHere) return;
        try {
          if (ifr.contentWindow === event.source) isFromHere = true;
        } catch (e) { void e; }
      });
      if (!isFromHere) return;
      if (nav1 && typeof nav1.slickNext === 'function') {
        nav1.slickNext();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [nav1]);

  return (
    <>
      <Navbar />
      <main>
        <section className="h-[250px] m-auto pt-[90px] page-title">
          <div className="text-center mb-[44px]">
            <h2 className="text-[#3D3D3D] font-semibold text-5xl mb-[15px]">
              {articleContent.title}
            </h2>
            <h3 className="text-[#ff6b2b] font-normal text-xl">{articleContent.intro}</h3>
          </div>
        </section>
        {(articleContent.content || (articleContent.image && !isVideoFile(articleContent.image))) && (
          <section className="max-w-[1300px] m-auto pt-[10px] mb-[100px]">
            <Box className="flex gap-10 pt-[60px] pb-[60px] md-l:flex-col">
              {articleContent.image && !isVideoFile(articleContent.image) && (
                <Box className="w-fit px-[80px] md-l:m-auto">
                  <img src={articleContent.image} alt={articleContent.title || ""} />
                </Box>
              )}
              {articleContent.content && (
                <Box className="w-fit md-l:mx-10">
                  <div
                    className="text-[#696969] font-normal text-xl leading-loose"
                    dangerouslySetInnerHTML={{ __html: articleContent.content }}
                  ></div>
                </Box>
              )}
            </Box>
          </section>
        )}

        {/* 影片區域 */}
        {articleContent.image && articleContent.image !== ""  && isVideoFile(articleContent.image) ?  (
          <section className="max-w-[1300px] m-auto pt-[10px] mb-[100px]" ref={triggerRef}>
            <Box className="flex justify-center pt-[60px] pb-[60px]">
              <div className="w-full max-w-[800px]">
                <video
                  ref={videoRef}
                  className="w-full h-auto rounded-lg shadow-lg"
                  controls
                  muted
                  playsInline
                  preload="metadata"
                >
                  <source src={articleContent.image} type="video/mp4" />
                  您的瀏覽器不支援影片播放。
                </video>
              </div>
            </Box>
          </section>
        ) : null}
        
        <section className="bg-yellow-nob mt-[-200px]"></section>

        {sliderContent.length !== 0 && (
          <div>
            <div className="text-center mb-[44px] mt-[-930px]">
              <h2 className="text-[#3D3D3D] font-normal text-5xl mb-[15px]">相簿</h2>
              <h3 className="text-[#696969] font-normal text-xl">Album</h3>
            </div>
            <Box sx={{ margin: "0 auto" }}>
              <div ref={mainSliderContainerRef} className="w-[50%] shadow-xl" style={{ margin: "0 auto", marginBottom: "25px", marginTop: "2rem" }}>
                <Slider
                  asNavFor={nav2}
                  focusOnSelect={false}
                  ref={(slider) => setNav1(slider)}
                  className="m-auto"
                  fade={true}
                  accessibility={false}
                  beforeChange={(oldIndex, newIndex) => setCurrentSlideIndex(newIndex)}
                  prevArrow={<NewsPrevArrow />}
                  nextArrow={<NewsNextArrow />}
                >
                  {sliderContent.map((item, index) => (
                    <div key={`SliderMain${index}`}>
                      <div 
                        className="aspect-[739/416] w-full relative overflow-hidden cursor-pointer"
                        onClick={() => {
                          if (sliderContent[currentSlideIndex]?.image && !isVideoFile(sliderContent[currentSlideIndex].image) && !isYouTubeUrl(sliderContent[currentSlideIndex].image)) {
                            handleImageClick(sliderContent[currentSlideIndex].image, sliderContent[currentSlideIndex].title || "", currentSlideIndex);
                          }
                        }}
                      >
                        {renderMedia(
                          item.image,
                          "",
                          "absolute top-0 left-0 bottom-0 right-0 w-auto h-full m-auto",
                          {},
                          false,
                          currentSlideIndex === index,
                          null,
                          false
                        )}
                      </div>
                      <h2 className="text-[#3D3D3D] font-semibold text-3xl mb-[15px]">
                        {item.title ? item.title : ""}
                      </h2>
                    </div>
                  ))}
                </Slider>
              </div>
                <Slider
                  asNavFor={nav1}
                  ref={(slider) => setNav2(slider)}
                  slidesToShow={5}
                  infinite={sliderContent.length > 5}
                  centerMode={sliderContent.length > 5}
                  swipeToSlide={true}
                  focusOnSelect={true}
                  beforeChange={(oldIndex, newIndex) => setCurrentSlideIndex(newIndex)}
                  className={"center"}
                  prevArrow={null}
                  nextArrow={null}
                  accessibility={false}
                  responsive={[
                    {
                      breakpoint: 1350,
                      settings: {
                        slidesToShow: 4,
                        infinite: sliderContent.length > 4,
                        centerMode: sliderContent.length > 4,
                      },
                    },
                    {
                      breakpoint: 991,
                      settings: {
                        slidesToShow: 3,
                        infinite: sliderContent.length > 3,
                        centerMode: sliderContent.length > 3,
                      },
                    },
                    {
                      breakpoint: 766,
                      settings: {
                        slidesToShow: 2,
                        infinite: sliderContent.length > 2,
                        centerMode: sliderContent.length > 2,
                      },
                    },
                    {
                      breakpoint: 375,
                      settings: {
                        slidesToShow: 1,
                        infinite: sliderContent.length > 1,
                        centerMode: sliderContent.length > 1,
                      },
                    },
                  ]}
                >
                  {sliderContent.map((item, index) => (
                  <div key={`SliderSub${index}`}>
                    <div className="aspect-[720/483] w-full relative overflow-hidden">
                      {renderMedia(
                        item.image,
                        "",
                        "absolute top-0 left-0 bottom-0 right-0 w-auto h-full m-auto",
                        {},
                        false,
                        false,
                        null,
                        false
                      )}
                    </div>
                    <h2 className="text-[#3D3D3D] font-semibold text-2xl table:text-xl mobile:text-lg break-words p-1">
                      {item.title ? item.title : ""}
                    </h2>
                  </div>
                  ))}
                </Slider>
            </Box>
          </div>
        )}
      </main>
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
        onClose={closeImageModal}
        images={sliderContent}
        currentIndex={modalImageIndex}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
      />
      <Footer />
    </>
  );
}

export default Page3;
